import Account from '@/lib/api/Account'
import WalletApi from '@/lib/api/Wallet'
import SmsApi from '@/lib/api/Sms'

function getBonusReceived(logonToken) {
  const walletApi = new WalletApi(logonToken)
  return walletApi
    .getBonusReceived()
    .then((response) => response.data)
    .catch((error) => {
      if (error.response.data && error.response.data.error) return { data: [] }
      return { data: { pending: true } }
    })
}

function getAmountToReceive(logonToken) {
  const account = new Account(logonToken)
  return account
    .getGiftsAmountToReceive(logonToken.token_payload.wed_id)
    .then((response) => response.data.data.amount)
    .catch(() => ({ pending: true }))
}

function getCashbackAmountToReceive(logonToken) {
  const walletApi = new WalletApi(logonToken)
  return walletApi
    .getCashbackAmountToReceive(logonToken.token_payload.wed_id)
    .then((response) => response.data.data.cashback_amount)
    .catch((error) => {
      if (error.response.status == 404) return 0
    })
}

function getTransactions(logonToken) {
  const walletApi = new WalletApi(logonToken)
  return walletApi
    .getTransactions()
    .then((response) => response.data)
    .catch((error) => {
      if (error.response.data && error.response.data.error) return { data: [] }
      return { data: { pending: true } }
    })
}

function getDebitTransactions(logonToken) {
  const walletApi = new WalletApi(logonToken)
  return walletApi
    .getDebitTransactions()
    .then((response) => response.data)
    .catch((error) => {
      if (error.response.data && error.response.data.error) return { data: [] }
      return { data: { pending: true } }
    })
}

function getCreditTransactions(logonToken) {
  const walletApi = new WalletApi(logonToken)
  return walletApi
    .getCreditTransactions()
    .then((response) => response.data)
    .catch((error) => {
      if (error.response.data && error.response.data.error) return { data: [] }
      return { data: { pending: true } }
    })
}

function getFutureTransactions(logonToken) {
  const walletApi = new WalletApi(logonToken)
  return walletApi
    .getFutureTransactions()
    .then((response) => response.data)
    .catch((error) => {
      if (error.response.data && error.response.data.error) return { data: [] }
      return { data: { pending: true } }
    })
}

function getWalletSummary(logonToken) {
  const walletApi = new WalletApi(logonToken)
  return walletApi.getSummary()
}

function getPhoneNumber(logonToken) {
  const smsApi = new SmsApi(logonToken)
  return smsApi
    .getPhoneNumber('wallet', logonToken.token_payload.user_id)
    .then((response) => response)
    .catch(() => ({
      pending: true,
    }))
}

function getExternalToken(logonToken) {
  const walletApi = new WalletApi(logonToken)
  return walletApi
    .getExternalToken()
    .then((response) => response.data.data)
    .catch(() => ({
      pending: true,
    }))
}

export default {
  computed: {
    logonToken() {
      return this.$store.state.app.logonToken
    },
    accessAuthorized() {
      // wedSiteInfo set at Vue.app (available for whole app)
      if (!this.$store.state.app.wedSiteInfo) {
        return null
      }
      // eslint-disable-next-line prettier/prettier
      return this.$store.state.app.wedSiteInfo.shared_menu.user_has_access_wallet
    },
    amountToReceive: {
      get() {
        return this.$store.state.wallet.amountToReceive
      },
      set(payload) {
        this.$store.dispatch('setAmountToReceive', payload)
      },
    },
    bonusReceived: {
      get() {
        return this.$store.state.wallet.bonusReceived
      },
      set(payload) {
        this.$store.dispatch('setBonusReceived', payload)
      },
    },
    cashbackAmountToReceive: {
      get() {
        return this.$store.state.wallet.cashbackAmountToReceive
      },
      set(payload) {
        this.$store.dispatch('setCashbackAmountToReceive', payload)
      },
    },
    transactions: {
      get() {
        return this.$store.state.wallet.transactions
      },
      set(payload) {
        this.$store.dispatch('setTransactions', payload)
      },
    },
    transactionsMeta: {
      get() {
        return this.$store.state.wallet.transactionsMeta
      },
      set(payload) {
        this.$store.dispatch('setTransactionsMeta', payload)
      },
    },
    debitTransactions: {
      get() {
        return this.$store.state.wallet.debitTransactions
      },
      set(payload) {
        this.$store.dispatch('setDebitTransactions', payload)
      },
    },
    creditTransactions: {
      get() {
        return this.$store.state.wallet.creditTransactions
      },
      set(payload) {
        this.$store.dispatch('setCreditTransactions', payload)
      },
    },
    futureTransactions: {
      get() {
        return this.$store.state.wallet.futureTransactions
      },
      set(payload) {
        this.$store.dispatch('setFutureTransactions', payload)
      },
    },
    walletSummary: {
      get() {
        return this.$store.state.wallet.walletSummary
      },
      set(payload) {
        this.$store.dispatch('setWalletSummary', payload)
      },
    },
    walletUpdatedAt: {
      get() {
        return this.$store.state.wallet.walletUpdatedAt
      },
      set(payload) {
        this.$store.dispatch('setWalletUpdatedAt', payload)
      },
    },
    externalToken: {
      get() {
        return this.$store.state.wallet.externalToken
      },
      set(payload) {
        this.$store.dispatch('setExternalToken', payload)
      },
    },
    phoneWalletInfo: {
      get() {
        return this.$store.state.wallet.phoneWalletInfo
      },
      set(payload) {
        this.$store.dispatch('setPhoneWalletInfo', payload)
      },
    },
  },

  watch: {
    logonToken(payload) {
      if (payload && payload.token) this.setData()
    },
    accessAuthorized(value) {
      if (value !== null) {
        this.checkAccess()
      }
    },
  },

  mounted() {
    if (this.logonToken && this.logonToken.token) this.setData()
    if (this.accessAuthorized !== null) this.checkAccess()
  },

  methods: {
    checkAccess() {
      if (!this.accessAuthorized) {
        return this.redirectWalletPending({ reason: 'permission' })
      }
    },

    redirectBalancePending(walletSummary) {
      if (this.$route.name != 'carteira' && walletSummary.pending)
        this.$router.push({ name: 'carteira' })
    },

    redirectNumberNotRegistered(hasNumberPhone) {
      if (
        (this.$route.name === 'carteira-saque-paypal' ||
          this.$route.name === 'carteira-saque' ||
          this.$route.name === 'carteira-paypal' ||
          this.$route.name === 'carteira-saque-pix' ||
          this.$route.name === 'carteira-cadastro-pix') &&
        !hasNumberPhone.receiver_identifier
      )
        this.$router.push({ name: 'carteira' })
    },

    redirectWalletPending(params = {}) {
      this.$router.push({ name: 'carteira-indisponivel', query: params })
    },

    setData() {
      if (
        this.logonToken &&
        (!this.logonToken.token_payload ||
          !this.logonToken.token_payload.wallet_id)
      ) {
        return this.redirectWalletPending()
      }

      if (this.$route.name === 'carteira') this.loading = false

      if (this.walletSummary.loading) {
        getWalletSummary(this.logonToken).then((walletSummary) => {
          this.redirectBalancePending(walletSummary)
          this.loading = false
          this.walletSummary = walletSummary
          this.walletUpdatedAt = new Date()
        })
      } else {
        this.redirectBalancePending(this.walletSummary)
        this.loading = false
        this.walletUpdatedAt = new Date()
      }

      if (this.phoneWalletInfo.loading) {
        getPhoneNumber(this.logonToken).then((response) => {
          this.phoneWalletInfo = response?.data
          this.redirectNumberNotRegistered(response.data)
        })
      }

      if (this.cashbackAmountToReceive.loading) {
        getCashbackAmountToReceive(this.logonToken).then(
          (cashbackAmountToReceive) => {
            this.cashbackAmountToReceive = cashbackAmountToReceive
          }
        )
      }

      if (this.externalToken.loading) {
        getExternalToken(this.logonToken).then((externalToken) => {
          this.externalToken = externalToken.token
        })
      }

      if (this.amountToReceive.loading) {
        getAmountToReceive(this.logonToken).then((amountToReceive) => {
          this.amountToReceive = amountToReceive
        })
      }

      if (this.transactions.loading) {
        getTransactions(this.logonToken).then((transactions) => {
          this.transactions = transactions.data
          this.transactionsMeta = transactions.meta
        })
      }

      if (this.debitTransactions.loading) {
        getDebitTransactions(this.logonToken).then((debitTransactions) => {
          this.debitTransactions = debitTransactions.data
        })
      }

      if (this.creditTransactions.loading) {
        getCreditTransactions(this.logonToken).then((creditTransactions) => {
          this.creditTransactions = creditTransactions.data
        })
      }

      if (this.futureTransactions.loading) {
        getFutureTransactions(this.logonToken).then((futureTransactions) => {
          this.futureTransactions = futureTransactions.data
        })
      }

      if (this.bonusReceived.loading) {
        getBonusReceived(this.logonToken).then((bonus) => {
          this.bonusReceived = bonus.data
        })
      }
    },
  },
}
